import { useState } from 'react';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { CAPACITY_BANDS, CAPACITY_LABELS, hasPermission, type CapacityBand, type HouseholdDetail } from '@hv/shared';
import { useSetAdvancement, useSetAssignment, useUsers } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { FollowUpPill } from '../components/badges';
import { citeAnchor } from '../assistant/citations';
import { useNotify } from '../components/Notify';
import { Section } from './Section';

export function WorkflowSection({ h }: { h: HouseholdDetail }) {
  const { can } = useAuth();
  const { notify, notifyError } = useNotify();
  const users = useUsers();
  const assign = useSetAssignment();
  const advancement = useSetAdvancement();
  const editable = can('households.write');
  const [followUp, setFollowUp] = useState(h.nextFollowUpAt ?? '');
  const [band, setBand] = useState<CapacityBand>(h.advancement.capacityBand);
  const [notes, setNotes] = useState(h.advancement.notes ?? '');

  const saveAssignment = (ownerUserId: string | null, nextFollowUpAt: string | null) =>
    assign.mutate(
      { id: h.id, ownerUserId, nextFollowUpAt },
      { onSuccess: () => notify('success', 'Assignment saved'), onError: (err) => notifyError(err, 'Saving assignment failed') },
    );

  const activeUsers = (users.data ?? []).filter((u) => (u.active && hasPermission(u.roles, 'households.read')) || u.id === h.ownerUserId);

  return (
    <Section title="Connection & Context" anchor="workflow">
      <Stack gap={2}>
        <TextField
          select
          size="small"
          label="Owner"
          value={h.ownerUserId ?? ''}
          disabled={!editable || assign.isPending}
          onChange={(e) => saveAssignment(e.target.value || null, h.nextFollowUpAt)}
          helperText={users.error ? 'Could not load users' : undefined}
          error={!!users.error}
        >
          <MenuItem value="">Unassigned</MenuItem>
          {activeUsers.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.displayName}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            type="date"
            label="Next follow-up"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            disabled={!editable}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {editable && (
            <>
              <Button aria-label="Set follow-up" disabled={!followUp || followUp === h.nextFollowUpAt || assign.isPending} onClick={() => saveAssignment(h.ownerUserId, followUp)}>
                Set
              </Button>
              <Button
                aria-label="Clear follow-up"
                disabled={!h.nextFollowUpAt || assign.isPending}
                onClick={() => {
                  setFollowUp('');
                  saveAssignment(h.ownerUserId, null);
                }}
              >
                Clear
              </Button>
            </>
          )}
          <FollowUpPill date={h.nextFollowUpAt} />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} {...citeAnchor('advancement')}>
          <TextField select size="small" label="Estimated capacity" value={band} onChange={(e) => setBand(e.target.value as CapacityBand)} disabled={!editable} sx={{ minWidth: 240 }}>
            {CAPACITY_BANDS.map((b) => (
              <MenuItem key={b} value={b}>
                {CAPACITY_LABELS[b]}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Advancement notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!editable} multiline maxRows={4} sx={{ flex: 1 }} />
        </Stack>
        {editable && (
          <Button
            sx={{ alignSelf: 'flex-start' }}
            disabled={(band === h.advancement.capacityBand && notes === (h.advancement.notes ?? '')) || advancement.isPending}
            onClick={() =>
              advancement.mutate(
                { id: h.id, capacityBand: band, notes: notes.trim() || null, solicitor1: h.advancement.solicitor1, solicitor2: h.advancement.solicitor2 },
                { onSuccess: () => notify('success', 'Advancement profile saved'), onError: (err) => notifyError(err, 'Saving advancement profile failed') },
              )
            }
          >
            Save advancement
          </Button>
        )}
        {(h.advancement.capacityRaw || h.advancement.lifetimeGiving !== null || h.advancement.solicitor1) && (
          <Typography variant="caption" color="text.secondary">
            {h.advancement.capacityRaw ? `Veracross capacity: ${h.advancement.capacityRaw}. ` : ''}
            {h.advancement.lifetimeGiving !== null ? `Lifetime giving: $${h.advancement.lifetimeGiving.toLocaleString()}. ` : ''}
            {h.advancement.solicitor1 ? `Solicitors: ${[h.advancement.solicitor1, h.advancement.solicitor2].filter(Boolean).join(', ')}.` : ''}
          </Typography>
        )}
      </Stack>
    </Section>
  );
}
